import { useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../app/store';
import { completeGoogleLogin, setOrgChoices } from '../../app/sessionSlice';
import { http } from '../../core/http/client';
import { config } from '../../core/config';
import { tokenStorage } from '../../core/auth/tokenStorage';
import type { LoginStep1Response } from '../../core/api/types';
import { DestinationPicker, firstDestination, countDestinations, destinationRoute, type DestinationChoice } from './DestinationPicker';
import { redirectTo } from '../../shared/navigation';

type GoogleApi = { accounts: { id: {
  initialize(options: { client_id: string; callback: (response: { credential: string }) => void }): void;
  renderButton(element: HTMLElement, options: Record<string, unknown>): void;
} } };
declare global { interface Window { google?: GoogleApi } }
let scriptPromise: Promise<void> | undefined;
function loadGoogle() {
  if (window.google) return Promise.resolve();
  return scriptPromise ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client'; script.async = true;
    script.onload = () => resolve();
    script.onerror = () => { script.remove(); scriptPromise = undefined; reject(new Error('Could not load Google sign-in. Please retry.')); };
    document.head.appendChild(script);
  });
}

export function GoogleButton({ label }: { label?: string }) {
  const { t } = useTranslation();
  const mobile = useMediaQuery('(max-width:600px)');
  const mount = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<{ credential: string; result: LoginStep1Response } | null>(null);
  const continuation = new URLSearchParams(location.search).get('continue');
  const from = (location.state as { from?: string } | null)?.from;

  const finish = async (credential: string, choice: DestinationChoice) => {
    setBusy(true); setError(null);
    try {
      let target: URL | undefined;
      if (continuation) {
        const allowed = new URL(import.meta.env.VITE_SSO_API_URL || (import.meta.env.DEV ? 'http://localhost:5100/api/v1/platform' : config.apiBaseUrl), window.location.origin);
        target = new URL(continuation, window.location.origin);
        if (target.origin !== allowed.origin || target.pathname !== allowed.pathname.replace(/\/$/, '') + '/auth/sso/authorize') throw new Error('Invalid sign-in return address.');
      }
      await dispatch(completeGoogleLogin({ credential, organizationId: choice.organizationId })).unwrap();
      setPending(null);
      if (target) redirectTo(target.href);
      else navigate(destinationRoute(choice.destination, from), { replace: true });
    } catch (caught) { setError(caught instanceof Error ? caught.message : 'Google sign-in failed.'); }
    finally { setBusy(false); }
  };
  const receive = async (credential: string) => {
    setBusy(true); setError(null);
    try {
      const { data: result } = await http().post<LoginStep1Response>('/auth/google', { credential }, { skipAuthRefresh: true });
      result.organizations = result.organizations.filter(o => o.destinations.length > 0);
      dispatch(setOrgChoices(result.organizations)); tokenStorage.setOrgChoices(result.organizations);
      if (continuation) result.organizations = result.organizations.filter(o => o.code === 'COMMUNITY');
      if (!result.organizations.length) throw new Error('This account has no active organizations.');
      const direct = countDestinations(result.organizations) === 1 || continuation ? firstDestination(result.organizations) : null;
      if (direct) await finish(credential, direct);
      else setPending({ credential, result });
    } catch (caught) { setError(caught instanceof Error ? caught.message : 'Google sign-in failed.'); }
    finally { setBusy(false); }
  };
  const receiveRef = useRef(receive); receiveRef.current = receive;
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await http().get<{ clientId?: string }>('/auth/google/config', { skipAuthRefresh: true });
        if (!data.clientId) throw new Error('Google sign-in requires a Google OAuth Client ID.');
        await loadGoogle();
        if (cancelled || !mount.current || !window.google) return;
        window.google.accounts.id.initialize({ client_id: data.clientId, callback: response => { if (!cancelled) void receiveRef.current(response.credential); } });
        window.google.accounts.id.renderButton(mount.current, { theme: 'outline', size: 'large', width: Math.min(486, mount.current.clientWidth || 320), text: label ? 'signup_with' : 'continue_with' });
      } catch (caught) { if (!cancelled) setError(caught instanceof Error ? caught.message : 'Google sign-in unavailable.'); }
    })();
    return () => { cancelled = true; };
  }, [label]);
  return <>
    <div ref={mount} aria-label={label ?? 'Continue with Google'} style={{ pointerEvents: busy ? 'none' : undefined }} />
    {busy && <p role="status">Signing in...</p>}
    {error && <div className="ax-alert error" role="alert">{error}</div>}
    {pending && <Dialog open fullScreen={mobile} maxWidth={false} sx={{ zIndex: 1500 }}
      aria-labelledby="ax-dest-title" onClose={() => { if (!busy) setPending(null); }}
      PaperProps={{ className: 'ax ax-google-picker' }}>
      <DestinationPicker organizations={pending.result.organizations} firstName={pending.result.user?.firstName} busy={busy} error={error} embedded
        onOpen={choice => void finish(pending.credential, choice)} onClose={busy ? undefined : () => setPending(null)}
        closeLabel={t('destinations.backToSignIn', 'Back to sign in')} />
    </Dialog>}
  </>;
}
